(function () {
  const toDateString = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  };

  class ClientIntakeForm extends HTMLElement {
    connectedCallback() {
      this.formElement = this.querySelector('[data-client-intake-form]');
      this.statusElement = this.querySelector('[data-intake-status]');
      this.submitButton = this.querySelector('[data-submit-consultation]');
      this.signatureCanvas = this.querySelector('[data-signature-canvas]');
      this.signatureTyped = this.querySelector('[name="signature_typed"]');
      this.clearSignatureButton = this.querySelector('[data-clear-signature]');
      this.phoneInput = this.querySelector('[name="phone"]');
      this.emailInput = this.querySelector('[name="email"]');
      this.startedAt = Date.now();
      this.hasSignatureInk = false;

      if (!this.formElement || !this.signatureCanvas || !this.submitButton) return;

      const dateField = this.querySelector('[name="signature_date"]');
      if (dateField && !dateField.value) dateField.value = toDateString(new Date());

      this.setupSignaturePad();
      this.formElement.addEventListener('submit', (event) => this.handleSubmit(event));
      this.clearSignatureButton?.addEventListener('click', () => this.clearSignature());
    }

    setupSignaturePad() {
      const canvas = this.signatureCanvas;
      const context = canvas.getContext('2d');
      let drawing = false;

      const resize = () => {
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const snapshot = this.hasSignatureInk ? canvas.toDataURL('image/png') : null;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = Math.round(rect.width * ratio);
        canvas.height = Math.round(rect.height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.strokeStyle = '#5b372f';
        context.lineWidth = 2;
        if (snapshot) {
          const image = new Image();
          image.onload = () => context.drawImage(image, 0, 0, rect.width, rect.height);
          image.src = snapshot;
        }
      };

      const point = (event) => {
        const rect = canvas.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
      };

      canvas.addEventListener('pointerdown', (event) => {
        drawing = true;
        this.hasSignatureInk = true;
        canvas.setPointerCapture(event.pointerId);
        const current = point(event);
        context.beginPath();
        context.moveTo(current.x, current.y);
        event.preventDefault();
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!drawing) return;
        const current = point(event);
        context.lineTo(current.x, current.y);
        context.stroke();
        event.preventDefault();
      });

      const finish = (event) => {
        if (!drawing) return;
        drawing = false;
        context.closePath();
        if (event?.pointerId !== undefined && canvas.hasPointerCapture(event.pointerId)) {
          canvas.releasePointerCapture(event.pointerId);
        }
      };

      canvas.addEventListener('pointerup', finish);
      canvas.addEventListener('pointercancel', finish);
      window.addEventListener('resize', resize, { passive: true });
      window.requestAnimationFrame(resize);
    }

    clearSignature() {
      const context = this.signatureCanvas.getContext('2d');
      context.clearRect(0, 0, this.signatureCanvas.width, this.signatureCanvas.height);
      this.hasSignatureInk = false;
      this.signatureTyped?.setCustomValidity('');
    }

    validateForm() {
      const hasContact = Boolean(this.phoneInput?.value.trim() || this.emailInput?.value.trim());
      this.phoneInput?.setCustomValidity(hasContact ? '' : 'Enter a phone number or email address.');

      const hasSignature = this.hasSignatureInk || Boolean(this.signatureTyped?.value.trim());
      this.signatureTyped?.setCustomValidity(hasSignature ? '' : 'Type your name or draw your signature.');

      return this.formElement.reportValidity();
    }

    setStatus(message, state) {
      if (!this.statusElement) return;
      this.statusElement.textContent = message;
      this.statusElement.dataset.state = state;
    }

    setBusy(isBusy) {
      this.submitButton.disabled = isBusy;
      this.submitButton.setAttribute('aria-busy', String(isBusy));
      this.submitButton.textContent = isBusy ? 'Submitting securely…' : 'Submit';
    }

    submissionPayload() {
      const fields = {};
      this.formElement.querySelectorAll('input, textarea').forEach((control) => {
        if (!control.name || control.name === 'website') return;
        if (control.type === 'radio') {
          if (control.checked) fields[control.name] = control.value;
          return;
        }
        if (control.type === 'checkbox') {
          fields[control.name] = control.checked;
          return;
        }
        fields[control.name] = control.value.trim();
      });

      return {
        fields,
        signature_png: this.hasSignatureInk ? this.signatureCanvas.toDataURL('image/png') : '',
        website: this.querySelector('[name="website"]')?.value || '',
        started_at: this.startedAt
      };
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (!this.validateForm()) return;

      const endpoint = String(this.dataset.submitEndpoint || '').trim();
      if (!endpoint) {
        this.setStatus('The secure submission service is not configured. Please contact Her Solis.', 'error');
        return;
      }

      this.setBusy(true);
      this.setStatus('Creating and securely sending your consultation PDF…', 'working');

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-store',
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.submissionPayload())
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.ok) throw new Error(result.error || 'The consultation could not be delivered.');

        this.setStatus('Submitted securely. Your consultation PDF has been sent to Steph at Her Solis.', 'success');
        this.submitButton.textContent = 'Submitted';
        this.submitButton.disabled = true;
      } catch (error) {
        console.error('Her Solis client consultation submission failed.', { message: error?.message });
        this.setStatus(error?.message || 'The consultation could not be delivered. Please try again.', 'error');
        this.setBusy(false);
      }
    }
  }

  if (!customElements.get('client-intake-form')) {
    customElements.define('client-intake-form', ClientIntakeForm);
  }
})();
